import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEventListener } from '../../utils/hooks';
import Portal from '../Portal/portal';
import ScrollLock from '../ScrollLock/scrollLock';
import ScrollbarWidthOffset from '../ScrollbarWidthOffset/scrollbarWidthOffset';
import Backdrop from './Backdrop/backdrop';
import ModalContent from './ModalContent/modalContent';
import { ModalContainer } from './modal.style';
import { ModalCloseReasons, ModalProps } from './modal.types';
import { BackdropProps } from './Backdrop/backdrop.types';

const DEFAULT_DATA_ATTR = { 'data-testid': 'modal' };

const Modal = ({
  ariaLabel,
  BackdropComponent = Backdrop,
  backdropEventType = 'onClick',
  backdropProps,
  children,
  className,
  CloseButtonComponent,
  closeButtonProps,
  ContentComponent = ModalContent,
  contentProps,
  dataAttr = {},
  disableBackdropClick = false,
  disablePortal = false,
  disableScrollbarWidthOffset = false,
  disableScrollLock = false,
  height,
  hideBackdrop = false,
  hideCloseButton = false,
  isCentered = false,
  isOpen = false,
  onClose,
  PortalComponent = Portal,
  portalProps,
  position,
  scrollType,
  width,
}: ModalProps) => {
  const ModalContentRef = useRef<HTMLElement | null>(null);
  const [previousActiveElement, setPreviousActiveElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (ModalContentRef.current) {
        if (!previousActiveElement) {
          setPreviousActiveElement(document.activeElement as HTMLElement);
        }
        ModalContentRef.current.focus({ preventScroll: true });
      }
    } else {
      if (previousActiveElement) {
        previousActiveElement.focus({ preventScroll: true });
        setPreviousActiveElement(null);
      }
    }
  }, [isOpen, previousActiveElement]);

  const onModalClose = useCallback((event: Event, reason: ModalCloseReasons) => onClose({ event, reason }), [onClose]);

  const onBackdropClick = useCallback(
    (event: Event) => {
      if (!disableBackdropClick) {
        onModalClose(event, 'backdropClick');
      }
    },
    [disableBackdropClick, onModalClose],
  );

  const onCloseButtonClick = useCallback((event: Event) => onModalClose(event, 'closeButtonClick'), [onModalClose]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const { key } = event;
      /**
       * `Esc` is IE/Edge specific
       * Reference: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key#example
       */
      if (isOpen && (key === 'Escape' || key === 'Esc')) {
        onModalClose(event, 'escapeKeyPress');
      }
    },
    [isOpen, onModalClose],
  );

  useEventListener('keydown', onKeyDown);

  const combinedDataAttr = { ...DEFAULT_DATA_ATTR, ...dataAttr };
  const combinedBackdropProps: BackdropProps = {
    ...backdropProps,
    [backdropEventType]: onBackdropClick,
  };

  return (
    <PortalComponent disablePortal={disablePortal} {...portalProps}>
      {isOpen && (
        <ModalContainer
          aria-label={ariaLabel}
          aria-modal
          className={className}
          isCentered={isCentered}
          role="dialog"
          scrollType={scrollType}
          {...combinedDataAttr}
        >
          {!disableScrollbarWidthOffset && <ScrollbarWidthOffset />}
          {!disableScrollLock && <ScrollLock />}
          {!hideBackdrop && <BackdropComponent {...combinedBackdropProps} />}
          <ContentComponent
            CloseButtonComponent={CloseButtonComponent}
            closeButtonProps={{
              ...closeButtonProps,
              handlers: {
                onClick: onCloseButtonClick,
              },
            }}
            contentRef={ModalContentRef}
            height={height}
            hideCloseButton={hideCloseButton}
            isCentered={isCentered}
            position={position}
            scrollType={scrollType}
            width={width}
            {...contentProps}
          >
            {children}
          </ContentComponent>
        </ModalContainer>
      )}
    </PortalComponent>
  );
};

export default React.memo(Modal);
